import type { Deal } from "@/components/atomic-crm/types";
import { arkaTenantBlueprint } from "@/components/atomic-crm/root/arkaTenantBlueprint";
import { dealHasMonthly, findCatalogProduct } from "./arkaCatalog";
import { isNaiuTenant } from "./tenant";
import {
  buildNaiuStageDatePatch,
  getNaiuStageTaskTemplates,
} from "./naiuStageTasks";

export interface StageTaskTemplate {
  autoKey: string;
  type: string;
  text: string;
  dueOffsetDays: number;
}

const CLOSED_REVENUE_STAGES = new Set(["won", "onboarding", "active"]);

export function isClosedRevenueStage(stage: string): boolean {
  return CLOSED_REVENUE_STAGES.has(stage);
}

export function todayIsoDate(now = new Date()): string {
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function addBusinessDays(start: Date, days: number): string {
  if (days <= 0) {
    return todayIsoDate(start);
  }
  const date = new Date(start);
  let added = 0;
  while (added < days) {
    date.setDate(date.getDate() + 1);
    const weekday = date.getDay();
    if (weekday !== 0 && weekday !== 6) {
      added += 1;
    }
  }
  return todayIsoDate(date);
}

export function dueDateFromOffset(offsetDays: number, now = new Date()): string {
  const date = new Date(now);
  date.setDate(date.getDate() + offsetDays);
  return date.toISOString();
}

function productLabel(deal: Deal): string {
  return findCatalogProduct(deal.product)?.label ?? deal.category ?? "el servicio";
}

export function getStageTaskTemplates(
  stage: string,
  deal: Deal,
): StageTaskTemplate[] {
  if (isNaiuTenant()) {
    return getNaiuStageTaskTemplates(stage, deal);
  }
  const name = deal.name;
  const product = productLabel(deal);

  if (stage === "captured") {
    return [
      {
        autoKey: "captured:first-contact",
        type: "whatsapp",
        text: `Primer contacto (WhatsApp o llamada) con ${name}.`,
        dueOffsetDays: 0,
      },
    ];
  }
  if (stage === "qualified") {
    return [
      {
        autoKey: "qualified:book-diagnosis",
        type: "diagnosis",
        text: `Agendar diagnóstico con ${name}.`,
        dueOffsetDays: 0,
      },
    ];
  }
  if (stage === "diagnosis_booked") {
    return [
      {
        autoKey: "diagnosis_booked:confirm",
        type: "meeting",
        text: `Confirmar asistencia al diagnóstico de ${name} (ideal: 24 h antes).`,
        dueOffsetDays: 0,
      },
    ];
  }
  if (stage === "diagnosis_done") {
    return [
      {
        autoKey: "diagnosis_done:send-proposal",
        type: "proposal",
        text: `Enviar propuesta de ${product} a ${name}.`,
        dueOffsetDays: 0,
      },
    ];
  }
  if (stage === "proposal") {
    return [
      {
        autoKey: "proposal:follow-3",
        type: "follow-up",
        text: `Seguimiento 1 (día 3) de la propuesta a ${name}. Sin acoso.`,
        dueOffsetDays: 3,
      },
      {
        autoKey: "proposal:follow-7",
        type: "follow-up",
        text: `Seguimiento 2 (día 7) de la propuesta a ${name}.`,
        dueOffsetDays: 7,
      },
      {
        autoKey: "proposal:follow-14",
        type: "follow-up",
        text: `Seguimiento 3 (día 14) de la propuesta a ${name}.`,
        dueOffsetDays: 14,
      },
    ];
  }
  if (stage === "negotiation") {
    return [
      {
        autoKey: "negotiation:close-points",
        type: "call",
        text: `Cerrar puntos pendientes y fecha de firma con ${name}.`,
        dueOffsetDays: 0,
      },
    ];
  }
  if (stage === "won") {
    const upsells = (arkaTenantBlueprint.wonFollowUp[deal.category] ?? []).map(
      (item, index) => ({
        autoKey: `won:upsell-${item.type}-${index}`,
        type: item.type,
        text: `${item.text} Negocio: ${name}.`,
        dueOffsetDays: 14,
      }),
    );
    return [
      {
        autoKey: "won:kickoff",
        type: "meeting",
        text: `Kickoff / contrato firmado: ${product} para ${name}.`,
        dueOffsetDays: 0,
      },
      ...upsells,
    ];
  }
  if (stage === "onboarding") {
    return [
      {
        autoKey: "onboarding:production",
        type: "onboarding",
        text: `Producción de ${product} para ${name}. Revisar fecha de entrega.`,
        dueOffsetDays: 0,
      },
    ];
  }
  if (stage === "active") {
    if (dealHasMonthly(deal.billing_type)) {
      return [
        {
          autoKey: "active:billing-notice",
          type: "follow-up",
          text: `Aviso de corte: 3 días antes de la mensualidad de ${name}.`,
          dueOffsetDays: 27,
        },
      ];
    }
    return [
      {
        autoKey: "active:follow-30",
        type: "follow-up",
        text: `Seguimiento a 30 días de ${product} con ${name}.`,
        dueOffsetDays: 30,
      },
    ];
  }
  return [];
}

export function buildStageDatePatch(
  deal: Deal,
  nextStage: string,
  now = new Date(),
): Partial<Deal> {
  if (isNaiuTenant()) {
    return buildNaiuStageDatePatch(deal, nextStage, now);
  }
  const patch: Partial<Deal> = {};
  if (isClosedRevenueStage(nextStage) && !deal.purchase_date) {
    patch.purchase_date = todayIsoDate(now);
  }
  if (isClosedRevenueStage(nextStage) && !deal.delivery_date) {
    const catalog = findCatalogProduct(deal.product);
    const days = catalog?.deliveryBusinessDays ?? 10;
    patch.delivery_date = addBusinessDays(now, days);
  }
  return patch;
}
